import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, Plus, Pencil, Inbox, FolderKanban, Mail, Phone, MapPin, Loader2, Briefcase, Clock, Upload, ImagePlus, Image as ImageIcon, GripVertical, Eye, FileText, AlertTriangle, Download, ShieldCheck, RefreshCw } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { sizedImg } from "@/lib/images";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, KeyboardSensor } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, rectSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ReplyDialog from "@/components/admin/ReplyDialog";
import NewSubmissionsWatcher from "@/components/admin/NewSubmissionsWatcher";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const STATUSES = ["nuovo", "contattato", "in_corso", "chiuso"];
const APPLICATION_STATUSES = ["nuovo", "contattato"];
const STATUS_STYLE = {
  nuovo: "bg-primary text-primary-foreground",
  contattato: "bg-foreground text-background",
  in_corso: "bg-secondary text-secondary-foreground",
  chiuso: "bg-green-600 text-white",
};
const EMPTY_PROJECT = {
  title: "",
  slug: "",
  category: "",
  location: "",
  year: "",
  description: "",
  intro: "",
  image: "",
  interventionsText: "",
  galleryText: "",
  captionsText: "",
  servicesText: "",
  wip: false,
  featured: false,
  ba_before: "",
  ba_after: "",
  ba_label: "Dal cantiere al risultato finale",
  ba_before_label: "PRIMA — cantiere",
  ba_after_label: "DOPO — ultimato",
  ba_enabled: false,
  video: "",
  video_enabled: false,
};

const slugify = (s) => (s || "")
  .toLowerCase()
  .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-z0-9\s-]/g, "")
  .trim()
  .replace(/\s+/g, "-")
  .replace(/-+/g, "-");

// --- GDPR: conservazione max 12 mesi per richieste e candidature (informativa privacy) ---
// Alert "expiring" all'ingresso dell'11° mese, alert "expired" dal giorno della scadenza.
const RETENTION_MONTHS = 12;
const WARN_AFTER_MONTHS = 11;
const DAY_MS = 86400000;

const retentionInfo = (createdAt) => {
  if (!createdAt) return null;
  const created = new Date(createdAt);
  if (isNaN(created.getTime())) return null;
  const expiry = new Date(created);
  expiry.setMonth(expiry.getMonth() + RETENTION_MONTHS);
  const daysLeft = Math.ceil((expiry.getTime() - Date.now()) / DAY_MS);
  const warn = new Date(created);
  warn.setMonth(warn.getMonth() + WARN_AFTER_MONTHS);
  const state = daysLeft <= 0 ? "expired" : Date.now() >= warn.getTime() ? "expiring" : "ok";
  return { state, daysLeft, expiry };
};

const retentionCounts = (quotes, applications) => {
  const c = { qExp: 0, qOld: 0, aExp: 0, aOld: 0 };
  quotes.forEach((q) => { const r = retentionInfo(q.created_at); if (r?.state === "expiring") c.qExp++; else if (r?.state === "expired") c.qOld++; });
  applications.forEach((a) => { const r = retentionInfo(a.created_at); if (r?.state === "expiring") c.aExp++; else if (r?.state === "expired") c.aOld++; });
  return c;
};

const RetentionBadge = ({ createdAt, testid }) => {
  const info = retentionInfo(createdAt);
  if (!info || info.state === "ok") return null;
  const expired = info.state === "expired";
  const expiryStr = info.expiry.toLocaleDateString("it-IT");
  return (
    <span
      data-testid={testid}
      title={expired ? `Conservazione scaduta il ${expiryStr} — cancella o anonimizza questi dati (GDPR)` : `Conservazione in scadenza il ${expiryStr}`}
      className={`inline-flex items-center gap-1 mt-2 font-mono text-[10px] uppercase tracking-widest px-2 py-1 border ${expired ? "border-destructive text-destructive" : "border-amber-500 text-amber-600"}`}
    >
      <AlertTriangle className="w-3 h-3" />
      {expired ? "Scaduta — da cancellare" : `Scade tra ${info.daysLeft} gg`}
    </span>
  );
};

const linesToArr = (t) => (t || "").split("\n").map((s) => s.trim()).filter(Boolean);
const arrToLines = (a) => (a || []).join("\n");

/**
 * Small thumbnail preview for an image URL/path. Falls back gracefully on error.
 */
function ImageThumb({ src, size = 40, alt = "" }) {
  const [error, setError] = useState(false);
  if (!src || error) {
    return (
      <div
        style={{ width: size, height: size }}
        className="shrink-0 grid place-items-center border border-dashed border-border bg-muted text-muted-foreground"
        title={src || "Nessuna immagine"}
      >
        <ImageIcon className="w-4 h-4" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      onError={() => setError(true)}
      style={{ width: size, height: size }}
      className="shrink-0 object-cover border border-border bg-muted"
      loading="lazy"
      data-testid="admin-thumb"
    />
  );
}



/**
 * Single sortable thumbnail with drag handle and remove button.
 */
function SortableThumb({ id, url, index, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : "auto",
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`sortable-thumb-${index}`}
      className="relative group touch-none"
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing"
        aria-label={`Trascina immagine ${index + 1} per riordinare`}
      >
        <ImageThumb src={url} size={52} alt={`img-${index + 1}`} />
        <span className="absolute top-0.5 left-0.5 inline-flex items-center justify-center w-4 h-4 bg-foreground/70 text-background opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          <GripVertical className="w-3 h-3" />
        </span>
        <span className="absolute bottom-0.5 left-0.5 font-mono text-[9px] bg-background/90 text-foreground px-1 pointer-events-none">{index + 1}</span>
      </div>
      <button
        type="button"
        onClick={onRemove}
        onPointerDown={(e) => e.stopPropagation()}
        aria-label="Rimuovi immagine"
        title="Rimuovi"
        data-testid={`gallery-thumb-remove-${index}`}
        className="absolute -top-1 -right-1 w-4 h-4 grid place-items-center bg-foreground text-background text-[9px] opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <Trash2 className="w-2.5 h-2.5" />
      </button>
    </div>
  );
}

/**
 * Sortable gallery strip: drag to reorder, syncs captions if lengths match.
 */
function SortableGalleryStrip({ galleryText, captionsText, onChange }) {
  const urls = (galleryText || "").split("\n").map((s) => s.trim()).filter(Boolean);
  const captions = (captionsText || "").split("\n").map((s) => s.trim()).filter(Boolean);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    // ids include index to ensure uniqueness even with duplicate URLs
    const oldIndex = parseInt(String(active.id).split("::")[0], 10);
    const newIndex = parseInt(String(over.id).split("::")[0], 10);
    if (Number.isNaN(oldIndex) || Number.isNaN(newIndex)) return;
    const newUrls = arrayMove(urls, oldIndex, newIndex);
    let newCaps = captions;
    if (captions.length === urls.length) {
      newCaps = arrayMove(captions, oldIndex, newIndex);
    }
    onChange({ galleryText: newUrls.join("\n"), captionsText: newCaps.join("\n") });
  };

  const handleRemove = (i) => {
    const newUrls = urls.filter((_, idx) => idx !== i);
    let newCaps = captions;
    if (captions.length === urls.length) newCaps = captions.filter((_, idx) => idx !== i);
    onChange({ galleryText: newUrls.join("\n"), captionsText: newCaps.join("\n") });
  };

  if (urls.length === 0) return null;
  const items = urls.map((u, i) => `${i}::${u}`);

  return (
    <div className="space-y-1.5">
      <p className="font-mono text-[10px] text-muted-foreground">
        Trascina le miniature per riordinarle. La galleria e le didascalie vengono sincronizzate.
      </p>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items} strategy={rectSortingStrategy}>
          <div data-testid="gallery-thumbs" className="flex flex-wrap gap-1.5 p-2 border border-border bg-muted/40">
            {urls.map((url, i) => (
              <SortableThumb
                key={items[i]}
                id={items[i]}
                url={url}
                index={i}
                onRemove={() => handleRemove(i)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

function UploadButton({ onUploaded, multiple = false, label = "Carica", testId = "upload-btn", accept = "image/*", endpoint = "/uploads" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      onUploaded(multiple ? urls : urls[0]);
      toast.success(`${urls.length} file caricat${urls.length > 1 ? "i" : "o"}.`);
    } catch (err) {
      toast.error("Upload fallito: " + (err?.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  return (
    <>
      <input
        type="file"
        accept={accept}
        multiple={multiple}
        ref={inputRef}
        onChange={handleFiles}
        className="hidden"
        data-testid={`${testId}-input`}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        data-testid={testId}
        className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-2.5 py-2 border border-border hover:border-foreground hover:bg-foreground hover:text-background transition-colors disabled:opacity-60 whitespace-nowrap"
        title="Carica file dal computer"
      >
        {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (multiple ? <ImagePlus className="w-3.5 h-3.5" /> : <Upload className="w-3.5 h-3.5" />)}
        <span className="hidden sm:inline">{uploading ? "..." : label}</span>
      </button>
    </>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="border border-border bg-card p-6">
      <div className="flex items-center justify-between mb-4">
        <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{label}</span>
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <div className="font-head font-black text-4xl tracking-tightest">{value}</div>
    </div>
  );
}

/** Draggable project card used in the admin Progetti tab. */
function SortableProjectCard({ p, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: p.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : "auto",
  };
  return (
    <div ref={setNodeRef} style={style} data-testid={`project-admin-${p.id}`} className="border border-border bg-card">
      <div className="relative aspect-[16/10] overflow-hidden">
        <img src={sizedImg(p.image, 600)} alt={p.title} loading="lazy" decoding="async" className="w-full h-full object-cover" />
        {p.wip && (
          <span data-testid={`project-admin-wip-${p.id}`} className="absolute top-2 left-2 inline-flex items-center gap-1 bg-primary text-primary-foreground font-mono text-[10px] uppercase tracking-widest px-2 py-1 shadow-sm" title="Cantiere in corso">
            <Loader2 className="w-3 h-3 animate-spin" /> Work in Progress
          </span>
        )}
        {/* Drag handle */}
        <button
          type="button"
          {...attributes}
          {...listeners}
          data-testid={`drag-project-${p.id}`}
          aria-label="Trascina per riordinare"
          className="absolute top-2 right-2 p-1.5 bg-black/60 backdrop-blur-sm text-white rounded cursor-grab active:cursor-grabbing hover:bg-primary transition-colors"
          title="Trascina per riordinare"
        >
          <GripVertical className="w-4 h-4" />
        </button>
      </div>
      <div className="p-4 sm:p-5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[10px] uppercase tracking-widest text-primary">{p.category}</span>
          {p.wip && (
            <span className="font-mono text-[10px] uppercase tracking-widest bg-primary/10 text-primary px-1.5 py-0.5">WIP</span>
          )}
        </div>
        <h3 className="font-head font-bold text-base sm:text-lg tracking-tight mt-1 break-words">{p.title}</h3>
        <p className="font-mono text-xs text-muted-foreground mt-1">{p.location} — {p.year}</p>
        <div className="flex items-center gap-1.5 mt-2 font-mono text-xs text-muted-foreground" title="Numero di visualizzazioni">
          <Eye className="w-3.5 h-3.5" />
          <span data-testid={`project-views-${p.id}`} className="tabular-nums">{(p.views || 0).toLocaleString("it-IT")}</span>
          <span className="uppercase tracking-widest text-[10px]">visite</span>
          {(p.quote_requests || 0) > 0 && (
            <span
              data-testid={`project-conversion-${p.id}`}
              className="ml-auto inline-flex items-center gap-1 text-primary font-semibold"
              title={`${p.quote_requests} preventiv${p.quote_requests === 1 ? 'o' : 'i'} richiest${p.quote_requests === 1 ? 'o' : 'i'} — conversion rate`}
            >
              <Mail className="w-3.5 h-3.5" />
              {(p.quote_requests || 0)}
              {(p.views || 0) > 0 && (
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">({Math.round(((p.quote_requests || 0) / (p.views || 1)) * 100)}%)</span>
              )}
            </span>
          )}
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={() => onEdit(p)} data-testid={`edit-project-${p.id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 border border-border py-2 font-mono text-[10px] sm:text-xs uppercase tracking-widest hover:border-foreground transition-colors">
            <Pencil className="w-3.5 h-3.5" /> Modifica
          </button>
          <button onClick={() => onDelete(p.id)} data-testid={`delete-project-${p.id}`} className="p-2 border border-border hover:border-destructive hover:text-destructive transition-colors">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [applications, setApplications] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [replyState, setReplyState] = useState({ open: false, email: "", kind: "quote", item: null });
  const openReply = (kind, item) => setReplyState({ open: true, email: item?.email || "", kind, item });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pForm, setPForm] = useState(EMPTY_PROJECT);
  const [saving, setSaving] = useState(false);
  const [privacyLog, setPrivacyLog] = useState([]);
  const [weekly, setWeekly] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [q, p, a] = await Promise.all([api.get("/quotes"), api.get("/projects"), api.get("/careers")]);
      setQuotes(q.data);
      setProjects(p.data);
      setApplications(a.data);
      // Registro cancellazioni automatiche GDPR (non critico: se fallisce, la dashboard resta usabile)
      try {
        const pl = await api.get("/admin/privacy-log");
        setPrivacyLog(pl.data?.log || []);
      } catch { /* ignora */ }
      // Andamento settimanale visite/richieste (non critico)
      try {
        const wk = await api.get("/admin/analytics/weekly?weeks=12");
        setWeekly(wk.data || null);
      } catch { /* ignora */ }
    } catch {
      toast.error("Errore nel caricamento dei dati.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/quotes/${id}`, { status });
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
      toast.success("Stato aggiornato.");
    } catch { toast.error("Errore aggiornamento stato."); }
  };

  const deleteQuote = async (id) => {
    try {
      await api.delete(`/quotes/${id}`);
      setQuotes((qs) => qs.filter((q) => q.id !== id));
      toast.success("Richiesta eliminata.");
    } catch { toast.error("Errore eliminazione."); }
  };

  const deleteApplication = async (id) => {
    try {
      await api.delete(`/careers/${id}`);
      setApplications((as) => as.filter((a) => a.id !== id));
      toast.success("Candidatura eliminata.");
    } catch { toast.error("Errore eliminazione."); }
  };

  const updateApplicationStatus = async (id, status) => {
    try {
      await api.patch(`/careers/${id}`, { status });
      setApplications((as) => as.map((a) => (a.id === id ? { ...a, status } : a)));
      toast.success("Stato candidatura aggiornato.");
    } catch { toast.error("Errore aggiornamento stato."); }
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); }
    catch { return ""; }
  };

  const openNew = () => { setEditing(null); setPForm(EMPTY_PROJECT); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    const ba = p.before_after || {};
    setPForm({
      title: p.title || "",
      slug: p.slug || "",
      category: p.category || "",
      location: p.location || "",
      year: p.year || "",
      description: p.description || "",
      intro: p.intro || "",
      image: p.image || "",
      interventionsText: arrToLines(p.interventions),
      galleryText: arrToLines(p.gallery),
      captionsText: arrToLines(p.captions),
      servicesText: p.services && p.services.length ? JSON.stringify(p.services, null, 2) : "",
      wip: !!p.wip,
      featured: !!p.featured,
      ba_before: ba.before || "",
      ba_after: ba.after || "",
      ba_label: ba.label || "Dal cantiere al risultato finale",
      ba_before_label: ba.before_label || "PRIMA — cantiere",
      ba_after_label: ba.after_label || "DOPO — ultimato",
      ba_enabled: !!(p.before_after && p.before_after.before && p.before_after.after),
      video: p.video || "",
      video_enabled: !!p.video,
    });
    setDialogOpen(true);
  };

  const saveProject = async () => {
    if (!pForm.title || !pForm.category || !pForm.image) {
      toast.error("Titolo, categoria e immagine sono obbligatori.");
      return;
    }
    let servicesArr = [];
    if (pForm.servicesText && pForm.servicesText.trim()) {
      try {
        servicesArr = JSON.parse(pForm.servicesText);
        if (!Array.isArray(servicesArr)) throw new Error();
      } catch {
        toast.error('Servizi: JSON non valido (deve essere [{"label":"...","to":"..."}])');
        return;
      }
    }
    const hasBA = pForm.ba_enabled && pForm.ba_before && pForm.ba_after;
    if (pForm.ba_enabled && !(pForm.ba_before && pForm.ba_after)) {
      toast.error("Slideshow Prima/Dopo attivo ma manca una delle due foto");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: pForm.title,
        slug: pForm.slug || slugify(pForm.title),
        category: pForm.category,
        location: pForm.location,
        year: pForm.year,
        description: pForm.description,
        intro: pForm.intro || null,
        image: pForm.image,
        interventions: linesToArr(pForm.interventionsText),
        gallery: linesToArr(pForm.galleryText),
        captions: linesToArr(pForm.captionsText),
        services: servicesArr,
        wip: !!pForm.wip,
        featured: !!pForm.featured,
        before_after: hasBA ? {
          before: pForm.ba_before,
          after: pForm.ba_after,
          label: pForm.ba_label || "",
          before_label: pForm.ba_before_label || "PRIMA",
          after_label: pForm.ba_after_label || "DOPO",
        } : null,
        video: (pForm.video_enabled && pForm.video) ? pForm.video : null,
      };
      if (editing) {
        const { data } = await api.put(`/projects/${editing.id}`, payload);
        setProjects((ps) => ps.map((p) => (p.id === editing.id ? data : p)));
        toast.success("Progetto aggiornato.");
      } else {
        const { data } = await api.post("/projects", payload);
        setProjects((ps) => [data, ...ps]);
        toast.success("Progetto aggiunto.");
      }
      setDialogOpen(false);
    } catch (e) {
      toast.error("Errore nel salvataggio: " + (e?.response?.data?.detail || e.message));
    }
    finally { setSaving(false); }
  };

  const deleteProject = async (id) => {
    try {
      await api.delete(`/projects/${id}`);
      setProjects((ps) => ps.filter((p) => p.id !== id));
      toast.success("Progetto eliminato.");
    } catch { toast.error("Errore eliminazione."); }
  };

  // Drag-and-drop reorder for projects in the Progetti tab
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const handleProjectDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = projects.findIndex((p) => p.id === active.id);
    const newIndex = projects.findIndex((p) => p.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(projects, oldIndex, newIndex);
    setProjects(reordered); // optimistic update
    try {
      await api.post("/projects/reorder", reordered.map((p, i) => ({ id: p.id, order: i })));
      toast.success("Ordine progetti salvato.");
    } catch {
      toast.error("Errore salvataggio ordine.");
    }
  };

  // Alert GDPR: conteggi scadenze conservazione (11° mese / scadute)
  const ret = retentionCounts(quotes, applications);
  const retTotal = ret.qExp + ret.qOld + ret.aExp + ret.aOld;
  const retExpired = ret.qOld + ret.aOld;

  // Export archivio: scarica uno ZIP con richieste + candidature in CSV (Excel-ready)
  const downloadArchive = async () => {
    try {
      const res = await api.get("/admin/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const aEl = document.createElement("a");
      aEl.href = url;
      aEl.download = `archivio-ghs-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(aEl);
      aEl.click();
      aEl.remove();
      URL.revokeObjectURL(url);
      toast.success("Archivio scaricato: richieste + candidature in formato Excel/CSV.");
    } catch {
      toast.error("Errore durante l'export dell'archivio.");
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 bg-background/90 backdrop-blur-xl border-b border-border">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 h-18 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/images/ghs-logo-icon.png?v=4"
              alt=""
              width="636"
              height="204"
              className="h-10 w-auto"
              loading="eager"
            />
            <span className="w-3 h-3 bg-primary block" />
            <div>
              <p className="font-head font-black tracking-tightest leading-none" data-testid="admin-brand">GREENHOUSE SOLUTIONS — ADMIN</p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NewSubmissionsWatcher onNewSubmissions={load} />
            <button onClick={load} data-testid="admin-refresh" title="Ricarica subito richieste, candidature e progetti" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border border-border px-4 py-2.5 hover:border-foreground transition-colors">
              <RefreshCw className="w-4 h-4" /> <span className="hidden sm:inline">Aggiorna</span>
            </button>
            <button onClick={downloadArchive} data-testid="export-archive" title="Scarica una copia locale di tutte le richieste e candidature (ZIP con CSV per Excel)" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border border-border px-4 py-2.5 hover:border-foreground transition-colors">
              <Download className="w-4 h-4" /> <span className="hidden sm:inline">Scarica archivio</span>
            </button>
            <button onClick={doLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border border-border px-4 py-2.5 hover:border-foreground transition-colors">
              <LogOut className="w-4 h-4" /> Esci
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <StatCard label="Richieste totali" value={quotes.length} icon={Inbox} />
          <StatCard label="Nuove" value={quotes.filter((q) => q.status === "nuovo").length} icon={Mail} />
          <StatCard label="Candidature" value={applications.length} icon={Briefcase} />
          <StatCard label="Progetti" value={projects.length} icon={FolderKanban} />
          <StatCard label="Visite progetti" value={projects.reduce((s, p) => s + (p.views || 0), 0).toLocaleString("it-IT")} icon={Eye} />
        </div>

        {/* Alert GDPR — conservazione max 12 mesi (informativa privacy) */}
        {retTotal > 0 && (
          <div
            data-testid="retention-alerts"
            className={`mb-6 border p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3 ${retExpired > 0 ? "border-destructive/60 bg-destructive/10" : "border-amber-500/60 bg-amber-500/10"}`}
          >
            <AlertTriangle className={`w-5 h-5 shrink-0 ${retExpired > 0 ? "text-destructive" : "text-amber-600"}`} />
            <div className="flex-1">
              <p className="font-head font-bold text-sm">
                Scadenze privacy — conservazione massima 12 mesi
              </p>
              <p className="font-mono text-[11px] text-muted-foreground mt-1 uppercase tracking-widest" data-testid="retention-alerts-summary">
                {[
                  ret.qExp > 0 && `${ret.qExp} richieste in scadenza`,
                  ret.qOld > 0 && `${ret.qOld} richieste scadute`,
                  ret.aExp > 0 && `${ret.aExp} candidature in scadenza`,
                  ret.aOld > 0 && `${ret.aOld} candidature scadute`,
                ].filter(Boolean).join(" · ")}
                {" — "}valuta la cancellazione per rispettare l'informativa privacy. I dati scaduti da oltre 30 giorni vengono eliminati automaticamente.
              </p>
            </div>
          </div>
        )}

        {/* Info privacy permanente — cancellazione automatica attiva */}
        <div data-testid="privacy-autodelete-info" className="mb-6 border border-border bg-card px-4 py-3 flex flex-col md:flex-row md:items-center gap-2 md:gap-3">
          <ShieldCheck className="w-4 h-4 text-green-600 shrink-0" />
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Privacy attiva: conservazione max 12 mesi, poi cancellazione automatica dopo 30 giorni di grazia.
            {privacyLog.length > 0 && (
              <span data-testid="privacy-autodelete-count" className="text-foreground">
                {" "}Cancellate automaticamente finora: {privacyLog.length} pratiche.
              </span>
            )}
          </p>
        </div>

        {/* Grafico storico settimanale — visite e richieste */}
        {weekly && (
          <div className="mb-10 border border-border bg-card p-5 md:p-6" data-testid="weekly-chart">
            <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-primary" />
                <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Andamento settimanale — ultime 12 settimane</p>
              </div>
              <div className="flex items-center gap-4 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-foreground inline-block" /> Visite</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-primary inline-block" /> Richieste</span>
              </div>
            </div>
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={weekly.weeks} margin={{ top: 5, right: 10, bottom: 0, left: -18 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fontFamily: "JetBrains Mono", fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={{ stroke: "hsl(var(--border))" }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fontFamily: "JetBrains Mono", fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                  <Tooltip
                    cursor={{ fill: "hsl(var(--muted))" }}
                    contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 11 }}
                    formatter={(value, name) => [value, name === "views" ? "Visite" : name === "quotes" ? "Richieste" : "Candidature"]}
                    labelFormatter={(l) => `Settimana dal ${l}`}
                  />
                  <Bar dataKey="views" fill="hsl(var(--foreground))" maxBarSize={28} />
                  <Line type="monotone" dataKey="quotes" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={{ r: 3, fill: "hsl(var(--primary))" }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            {weekly.views_tracking_since && (
              <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                // Visite tracciate in dettaglio dal {new Date(weekly.views_tracking_since).toLocaleDateString("it-IT")} — le richieste hanno storico completo.
              </p>
            )}
          </div>
        )}

        {/* Top 3 progetti più visti + conversion */}
        {projects.some((p) => (p.views || 0) > 0) ? (
          <div className="mb-10 border border-border bg-card p-5 md:p-6" data-testid="top-projects-podium">
            <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-primary" />
                <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Progetti più visti — Top 3 automatico ogni settimana</p>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                {(() => {
                  const totalViews = projects.reduce((s, p) => s + (p.views || 0), 0);
                  const totalConv = projects.reduce((s, p) => s + (p.quote_requests || 0), 0);
                  const rate = totalViews > 0 ? Math.round((totalConv / totalViews) * 100) : 0;
                  return (
                    <div className="flex items-center gap-2 font-mono text-xs text-muted-foreground" data-testid="global-conversion">
                      <Mail className="w-4 h-4 text-primary" />
                      <span className="uppercase tracking-widest">Conversion totale:</span>
                      <span className="font-head font-black text-lg tracking-tight text-foreground">{totalConv}</span>
                      <span className="uppercase tracking-widest">preventivi</span>
                      <span className="text-primary font-semibold">({rate}%)</span>
                    </div>
                  );
                })()}
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const { data } = await api.post("/admin/weekly-report/preview");
                      if (data?.email_sent) {
                        toast.success(`Anteprima report inviata a ${data.recipients?.join(", ") || "amministrazione"}`);
                      } else {
                        toast.warning("Report generato ma email non configurata (EMERGENT_EMAIL_KEY mancante)");
                      }
                    } catch {
                      toast.error("Errore nell'invio dell'anteprima report");
                    }
                  }}
                  data-testid="preview-report-btn"
                  className="inline-flex items-center gap-2 border border-border text-muted-foreground font-mono text-[10px] uppercase tracking-widest px-3 py-2 hover:border-foreground hover:text-foreground transition-colors"
                  title="Invia subito l'email di report all'amministrazione (senza aspettare la settimana)"
                >
                  <Mail className="w-3.5 h-3.5" />
                  Anteprima Report
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const { data } = await api.post("/admin/auto-featured/run");
                      const top3 = (data?.top3 || []).join(", ") || "nessuno con preventivi";
                      toast.success(`Top 3 rigenerato: ${top3}`);
                      // reload projects to reflect new order
                      const r = await api.get("/projects");
                      setProjects(r.data || []);
                    } catch {
                      toast.error("Errore nell'aggiornamento del Top 3");
                    }
                  }}
                  data-testid="run-auto-featured-btn"
                  className="inline-flex items-center gap-2 border border-primary text-primary font-mono text-[10px] uppercase tracking-widest px-3 py-2 hover:bg-primary hover:text-primary-foreground transition-colors"
                  title="Rigenera manualmente Top 3 in evidenza in base ai preventivi ricevuti"
                >
                  Aggiorna Top 3
                </button>
                {typeof window !== "undefined" && !/preview\.emergentagent/i.test(window.location.hostname) && (
                  <button
                    type="button"
                    onClick={async () => {
                      if (!window.confirm("Vuoi sincronizzare i contenuti dei progetti da PREVIEW a PRODUCTION?\n\nCosa viene aggiornato:\n• Foto, testi, gallerie, ordinamento\n\nCosa NON viene toccato:\n• Visite e conversioni (restano quelle reali di production)")) return;
                      try {
                        toast.info("Sincronizzazione in corso…");
                        const { data } = await api.post("/admin/sync-content");
                        const parts = [];
                        if (data.created?.length) parts.push(`${data.created.length} creati`);
                        if (data.updated?.length) parts.push(`${data.updated.length} aggiornati`);
                        if (data.unchanged_count) parts.push(`${data.unchanged_count} invariati`);
                        toast.success("Contenuti sincronizzati", {
                          description: parts.join(" · ") || "Nessun cambiamento",
                          duration: 8000,
                        });
                        const r = await api.get("/projects");
                        setProjects(r.data || []);
                      } catch (e) {
                        toast.error("Errore sincronizzazione", { description: e?.response?.data?.detail || String(e) });
                      }
                    }}
                    data-testid="sync-content-btn"
                    className="inline-flex items-center gap-2 border border-border text-muted-foreground font-mono text-[10px] uppercase tracking-widest px-3 py-2 hover:border-foreground hover:text-foreground transition-colors"
                    title="Sincronizza foto, testi e ordinamento dei progetti dall'ambiente preview a production. Non tocca analytics."
                  >
                    Sincronizza contenuti
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[...projects].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 3).map((p, i) => {
                const conv = p.views > 0 ? Math.round(((p.quote_requests || 0) / p.views) * 100) : 0;
                return (
                  <div key={p.id} data-testid={`top-project-${i + 1}`} className="flex items-center gap-3 bg-background/60 p-3">
                    <span className={`font-head font-black text-2xl md:text-3xl tracking-tightest w-8 shrink-0 ${i === 0 ? "text-primary" : "text-muted-foreground"}`}>#{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <p className="font-head font-bold text-sm truncate">{p.title}</p>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                        {(p.views || 0).toLocaleString("it-IT")} visite · {p.quote_requests || 0} preventivi{p.views > 0 && ` · ${conv}%`}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="mb-10 border border-dashed border-border bg-card p-6 text-center" data-testid="empty-analytics">
            <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              // Analytics vuoto — non ci sono ancora visite ai progetti
            </p>
            <p className="font-mono text-[10px] text-muted-foreground mt-2">
              Il Top 3 in evidenza verrà rigenerato automaticamente ogni settimana in base alle conversioni (visita → richiesta preventivo).
            </p>
            <button
              type="button"
              onClick={async () => {
                try {
                  const { data } = await api.post("/admin/weekly-report/preview");
                  if (data?.email_sent) {
                    toast.success(`Anteprima report inviata a ${data.recipients?.join(", ") || "amministrazione"}`);
                  } else {
                    toast.warning("Report generato ma email non configurata");
                  }
                } catch {
                  toast.error("Errore nell'invio dell'anteprima report");
                }
              }}
              data-testid="preview-report-empty-btn"
              className="mt-4 inline-flex items-center gap-2 border border-border text-muted-foreground font-mono text-[10px] uppercase tracking-widest px-4 py-2 hover:border-foreground hover:text-foreground transition-colors"
            >
              <Mail className="w-3.5 h-3.5" />
              Prova invio anteprima report
            </button>
          </div>
        )}

        {loading ? (
          <div className="py-32 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : (
          <Tabs defaultValue="richieste">
            <TabsList className="rounded-none bg-secondary/60 mb-8 flex w-full h-auto items-stretch justify-start overflow-x-auto no-scrollbar snap-x snap-mandatory md:w-auto md:inline-flex md:justify-center">
              <TabsTrigger value="richieste" data-testid="tab-richieste" className="shrink-0 snap-start rounded-none font-mono text-[10px] md:text-xs uppercase tracking-widest px-3 md:px-4 py-2 whitespace-nowrap">
                Richieste
                {quotes.filter((q) => q.status === "nuovo").length > 0 && (
                  <span data-testid="badge-nuove-quotes" className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-primary text-primary-foreground font-mono text-[10px] tabular-nums">
                    {quotes.filter((q) => q.status === "nuovo").length}
                  </span>
                )}
                {(ret.qExp + ret.qOld) > 0 && (
                  <span data-testid="tab-retention-dot-richieste" title="Alert scadenze privacy (conservazione 12 mesi)" className={`ml-2 w-2 h-2 inline-block rounded-full ${ret.qOld > 0 ? "bg-destructive" : "bg-amber-500"}`} />
                )}
              </TabsTrigger>
              <TabsTrigger value="candidature" data-testid="tab-candidature" className="shrink-0 snap-start rounded-none font-mono text-[10px] md:text-xs uppercase tracking-widest px-3 md:px-4 py-2 whitespace-nowrap">
                Candidature
                {applications.filter((a) => (a.status || "nuovo") === "nuovo").length > 0 && (
                  <span data-testid="badge-candidature" className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-primary text-primary-foreground font-mono text-[10px] tabular-nums">
                    {applications.filter((a) => (a.status || "nuovo") === "nuovo").length}
                  </span>
                )}
                {(ret.aExp + ret.aOld) > 0 && (
                  <span data-testid="tab-retention-dot-candidature" title="Alert scadenze privacy (conservazione 12 mesi)" className={`ml-2 w-2 h-2 inline-block rounded-full ${ret.aOld > 0 ? "bg-destructive" : "bg-amber-500"}`} />
                )}
              </TabsTrigger>
              <TabsTrigger value="progetti" data-testid="tab-progetti" className="shrink-0 snap-start rounded-none font-mono text-[10px] md:text-xs uppercase tracking-widest px-3 md:px-4 py-2 whitespace-nowrap">
                Progetti
              </TabsTrigger>
            </TabsList>

            <TabsContent value="richieste">
              <div className="border border-border bg-card overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border">
                      <TableHead className="font-mono text-xs uppercase tracking-widest">Cliente</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-widest">Contatti</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-widest">Servizio</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-widest">Stato</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-widest text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {quotes.length === 0 && (
                      <TableRow><TableCell colSpan={5} className="text-center py-16 font-mono text-sm text-muted-foreground">Nessuna richiesta ricevuta.</TableCell></TableRow>
                    )}
                    {quotes.map((q) => (
                      <TableRow key={q.id} data-testid={`quote-row-${q.id}`} className="border-border align-top md:hover:bg-muted/40 transition-colors">
                        <TableCell onClick={() => setSelectedItem({ type: "quote", data: q })} data-testid={`open-quote-${q.id}`} className="cursor-pointer group" title="Clicca per il dettaglio completo">
                          <p className="font-bold underline decoration-dotted underline-offset-4 md:group-hover:text-primary transition-colors">{q.name}</p>
                          <p className="font-mono text-xs text-muted-foreground mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" />{q.location || "—"}</p>
                          {q.message && <p className="text-xs text-muted-foreground mt-2 max-w-xs line-clamp-2">{q.message}</p>}
                          <RetentionBadge createdAt={q.created_at} testid={`retention-quote-${q.id}`} />
                        </TableCell>
                        <TableCell onClick={() => setSelectedItem({ type: "quote", data: q })} className="font-mono text-xs cursor-pointer">
                          <p className="flex items-center gap-1"><Mail className="w-3 h-3" />{q.email}</p>
                          <p className="flex items-center gap-1 mt-1"><Phone className="w-3 h-3" />{q.phone}</p>
                        </TableCell>
                        <TableCell onClick={() => setSelectedItem({ type: "quote", data: q })} className="text-sm max-w-[180px] cursor-pointer">{q.service}</TableCell>
                        <TableCell>
                          <Badge className={`rounded-none font-mono text-[10px] uppercase tracking-widest ${STATUS_STYLE[q.status] || ""}`}>{q.status}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-2">
                            <Select value={q.status} onValueChange={(v) => updateStatus(q.id, v)}>
                              <SelectTrigger data-testid={`status-select-${q.id}`} className="rounded-none h-9 w-36 font-mono text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {STATUSES.map((s) => <SelectItem key={s} value={s} className="font-mono text-xs">{s}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <button type="button" onClick={(e) => { e.stopPropagation(); openReply("quote", q); }} data-testid={`reply-quote-${q.id}`} className="p-2 border border-border md:hover:border-primary md:hover:text-primary transition-colors" aria-label="Rispondi via email" title={`Rispondi a ${q.email}`}><Mail className="w-4 h-4 pointer-events-none" /></button>
                            <button onClick={() => deleteQuote(q.id)} data-testid={`delete-quote-${q.id}`} className="p-2 border border-border md:hover:border-destructive md:hover:text-destructive transition-colors" aria-label="Elimina"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="candidature">
              <div className="border border-border bg-card overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border">
                      <TableHead className="font-mono text-xs uppercase tracking-widest">Candidato</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-widest">Contatti</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-widest">Mansione</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-widest">Stato</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-widest">Data</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-widest text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {applications.length === 0 && (
                      <TableRow><TableCell colSpan={6} className="text-center py-16 font-mono text-sm text-muted-foreground">Nessuna candidatura ricevuta.</TableCell></TableRow>
                    )}
                    {applications.map((a) => (
                      <TableRow key={a.id} data-testid={`application-row-${a.id}`} className="border-border align-top md:hover:bg-muted/40 transition-colors">
                        <TableCell onClick={() => setSelectedItem({ type: "career", data: a })} data-testid={`open-application-${a.id}`} className="cursor-pointer group" title="Clicca per il dettaglio completo">
                          <p className="font-bold underline decoration-dotted underline-offset-4 md:group-hover:text-primary transition-colors">{a.name}</p>
                          {a.experience && <p className="font-mono text-xs text-muted-foreground mt-1">Esperienza: {a.experience}</p>}
                          {a.availability && <p className="font-mono text-xs text-muted-foreground mt-1">Disp.: {a.availability}</p>}
                          {a.cv_url && (
                            <a
                              href={a.cv_url}
                              download={a.cv_filename || undefined}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`career-cv-${a.id}`}
                              className="inline-flex items-center gap-1.5 mt-2 font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
                            >
                              <FileText className="w-3 h-3" /> Scarica CV
                            </a>
                          )}
                          {a.message && <p className="text-xs text-muted-foreground mt-2 max-w-xs line-clamp-2">{a.message}</p>}
                          <RetentionBadge createdAt={a.created_at} testid={`retention-application-${a.id}`} />
                        </TableCell>
                        <TableCell onClick={() => setSelectedItem({ type: "career", data: a })} className="font-mono text-xs cursor-pointer">
                          <p className="flex items-center gap-1"><Mail className="w-3 h-3" />{a.email}</p>
                          <p className="flex items-center gap-1 mt-1"><Phone className="w-3 h-3" />{a.phone}</p>
                        </TableCell>
                        <TableCell onClick={() => setSelectedItem({ type: "career", data: a })} className="text-sm max-w-[200px] cursor-pointer">{a.role}</TableCell>
                        <TableCell>
                          <Badge className={`rounded-none font-mono text-[10px] uppercase tracking-widest ${STATUS_STYLE[a.status || "nuovo"] || ""}`}>{a.status || "nuovo"}</Badge>
                        </TableCell>
                        <TableCell onClick={() => setSelectedItem({ type: "career", data: a })} className="font-mono text-xs text-muted-foreground cursor-pointer"><span className="flex items-center gap-1"><Clock className="w-3 h-3" />{fmtDate(a.created_at)}</span></TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-2">
                            <Select value={APPLICATION_STATUSES.includes(a.status) ? a.status : "nuovo"} onValueChange={(v) => updateApplicationStatus(a.id, v)}>
                              <SelectTrigger data-testid={`career-status-select-${a.id}`} className="rounded-none h-9 w-36 font-mono text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {APPLICATION_STATUSES.map((s) => <SelectItem key={s} value={s} className="font-mono text-xs">{s}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <button type="button" onClick={(e) => { e.stopPropagation(); openReply("career", a); }} data-testid={`reply-application-${a.id}`} className="p-2 border border-border md:hover:border-primary md:hover:text-primary transition-colors" aria-label="Rispondi via email" title={`Rispondi a ${a.email}`}><Mail className="w-4 h-4 pointer-events-none" /></button>
                            <button onClick={() => deleteApplication(a.id)} data-testid={`delete-application-${a.id}`} className="p-2 border border-border md:hover:border-destructive md:hover:text-destructive transition-colors" aria-label="Elimina"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="progetti">
              <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
                <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  <GripVertical className="w-3.5 h-3.5 inline-block align-middle mr-1" />
                  Trascina le card per riordinare — l'ordine viene salvato automaticamente
                </p>
                <button onClick={openNew} data-testid="add-project-btn" className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-mono text-xs uppercase tracking-widest px-5 py-3 hover:bg-foreground transition-colors">
                  <Plus className="w-4 h-4" /> Nuovo Progetto
                </button>
              </div>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleProjectDragEnd}>
                <SortableContext items={projects.map((p) => p.id)} strategy={rectSortingStrategy}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {projects.map((p) => (
                      <SortableProjectCard key={p.id} p={p} onEdit={openEdit} onDelete={deleteProject} />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </TabsContent>
          </Tabs>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-none max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="project-dialog">
          <DialogHeader><DialogTitle className="font-head font-black text-2xl tracking-tightest">{editing ? "Modifica Progetto" : "Nuovo Progetto"}</DialogTitle></DialogHeader>
          <div className="space-y-5 py-2">
            {/* Basic */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { k: "title", l: "Titolo *", ph: "Villa Residenziale" },
                { k: "slug", l: "Slug (auto)", ph: "villa-residenziale" },
                { k: "category", l: "Categoria *", ph: "Ristrutturazione" },
                { k: "location", l: "Località", ph: "Monopoli" },
                { k: "year", l: "Anno", ph: "2026" },
              ].map((f) => (
                <div key={f.k} className="space-y-1.5">
                  <Label className="font-mono text-xs uppercase tracking-widest">{f.l}</Label>
                  <Input data-testid={`project-${f.k}`} value={pForm[f.k]} onChange={(e) => setPForm((s) => ({ ...s, [f.k]: e.target.value }))} placeholder={f.ph} className="rounded-none" />
                </div>
              ))}
              <div className="space-y-1.5">
                <Label className="font-mono text-xs uppercase tracking-widest">URL Immagine hero *</Label>
                <div className="flex gap-2 items-center">
                  <ImageThumb src={pForm.image} size={40} alt="hero" />
                  <Input data-testid="project-image" value={pForm.image} onChange={(e) => setPForm((s) => ({ ...s, image: e.target.value }))} placeholder="https://... o carica" className="rounded-none flex-1" />
                  <UploadButton testId="upload-hero" onUploaded={(url) => setPForm((s) => ({ ...s, image: url }))} label="Carica" />
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="font-mono text-xs uppercase tracking-widest">Descrizione breve (card)</Label>
              <Textarea data-testid="project-description" value={pForm.description} onChange={(e) => setPForm((s) => ({ ...s, description: e.target.value }))} rows={2} className="rounded-none" placeholder="Sintesi mostrata nella card lista..." />
            </div>

            <div className="space-y-1.5">
              <Label className="font-mono text-xs uppercase tracking-widest">Intro (paragrafi lunghi, separa con riga vuota)</Label>
              <Textarea data-testid="project-intro" value={pForm.intro} onChange={(e) => setPForm((s) => ({ ...s, intro: e.target.value }))} rows={5} className="rounded-none" placeholder="Testo esteso mostrato nella pagina di dettaglio..." />
            </div>

            <div className="space-y-1.5">
              <Label className="font-mono text-xs uppercase tracking-widest">Interventi (uno per riga)</Label>
              <Textarea data-testid="project-interventions" value={pForm.interventionsText} onChange={(e) => setPForm((s) => ({ ...s, interventionsText: e.target.value }))} rows={5} className="rounded-none" placeholder="Realizzazione di parete ventilata&#10;Sostituzione infissi&#10;..." />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label className="font-mono text-xs uppercase tracking-widest">Galleria (una URL/path per riga)</Label>
                <UploadButton
                  testId="upload-gallery"
                  multiple
                  label="Carica foto"
                  onUploaded={(urls) => setPForm((s) => ({ ...s, galleryText: [s.galleryText, ...urls].filter(Boolean).join("\n") }))}
                />
              </div>
              <p className="font-mono text-[10px] text-muted-foreground">
                Puoi selezionare <strong>più foto in una volta</strong> tenendo premuto Ctrl (Windows) o Cmd (Mac). Verranno caricate in sequenza e aggiunte alla galleria.
              </p>
              <SortableGalleryStrip
                galleryText={pForm.galleryText}
                captionsText={pForm.captionsText}
                onChange={({ galleryText, captionsText }) => setPForm((s) => ({ ...s, galleryText, captionsText }))}
              />
              <Textarea data-testid="project-gallery" value={pForm.galleryText} onChange={(e) => setPForm((s) => ({ ...s, galleryText: e.target.value }))} rows={4} className="rounded-none font-mono text-xs" placeholder="/images/canonico/6.jpg&#10;/images/canonico/7.jpg&#10;..." />
            </div>

            <div className="space-y-1.5">
              <Label className="font-mono text-xs uppercase tracking-widest">Didascalie (una per riga, stesso ordine della galleria)</Label>
              <Textarea data-testid="project-captions" value={pForm.captionsText} onChange={(e) => setPForm((s) => ({ ...s, captionsText: e.target.value }))} rows={5} className="rounded-none" placeholder="Vista serale dell'angolo&#10;Facciata ultimata&#10;..." />
            </div>

            <div className="space-y-1.5">
              <Label className="font-mono text-xs uppercase tracking-widest">Servizi collegati (JSON, opzionale)</Label>
              <Textarea data-testid="project-services" value={pForm.servicesText} onChange={(e) => setPForm((s) => ({ ...s, servicesText: e.target.value }))} rows={4} className="rounded-none font-mono text-xs" placeholder='[{"label":"Pareti Ventilate","to":"/pareti-ventilate"}]' />
            </div>

            {/* VIDEO */}
            <div className={`border-2 p-4 space-y-3 transition-colors ${pForm.video_enabled ? "border-primary/40 bg-primary/5" : "border-border bg-muted/30"}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className={`font-mono text-xs uppercase tracking-widest ${pForm.video_enabled ? "text-primary" : "text-muted-foreground"}`}>// Video del cantiere (opzionale)</p>
                  <p className="font-mono text-[10px] text-muted-foreground mt-1">
                    {pForm.video_enabled
                      ? "Se carichi un video, verrà mostrato nella pagina progetto prima della galleria. Formati: mp4, webm, mov (max 100 MB)."
                      : "Attiva questo blocco solo se il progetto ha un video da mostrare. Se disattivato, la sezione non appare sulla pagina progetto."}
                  </p>
                </div>
                {/* Toggle switch */}
                <button
                  type="button"
                  role="switch"
                  aria-checked={pForm.video_enabled}
                  onClick={() => setPForm((s) => ({ ...s, video_enabled: !s.video_enabled }))}
                  data-testid="video-enabled-toggle"
                  className={`shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${pForm.video_enabled ? "bg-primary" : "bg-muted-foreground/40"}`}
                  title={pForm.video_enabled ? "Disattiva video" : "Attiva video"}
                >
                  <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${pForm.video_enabled ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </div>
              {pForm.video_enabled && (<>
              <div className="flex gap-2 items-center">
                <Input data-testid="project-video" value={pForm.video} onChange={(e) => setPForm((s) => ({ ...s, video: e.target.value }))} placeholder="/videos/... o URL esterno" className="rounded-none flex-1 font-mono text-xs" />
                <UploadButton
                  testId="upload-video"
                  label="Carica video"
                  accept="video/mp4,video/webm,video/quicktime"
                  endpoint="/uploads/video"
                  onUploaded={(url) => setPForm((s) => ({ ...s, video: url }))}
                />
                {pForm.video && (
                  <button type="button" onClick={() => setPForm((s) => ({ ...s, video: "" }))} data-testid="clear-video" className="p-2 border border-border hover:border-destructive hover:text-destructive transition-colors" title="Rimuovi video">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              {pForm.video && (
                <video src={pForm.video} controls playsInline preload="metadata" className="w-full max-h-52 bg-black" />
              )}
              </>)}
            </div>

            {/* Before/After */}
            <div className={`border-2 p-4 space-y-3 transition-colors ${pForm.ba_enabled ? "border-primary/40 bg-primary/5" : "border-border bg-muted/30"}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className={`font-mono text-xs uppercase tracking-widest ${pForm.ba_enabled ? "text-primary" : "text-muted-foreground"}`}>// Slideshow Prima/Dopo (opzionale)</p>
                  <p className="font-mono text-[10px] text-muted-foreground mt-1">
                    {pForm.ba_enabled
                      ? "Carica una foto \"prima\" e una \"dopo\" per attivare il confronto interattivo. Le etichette sono già pre-compilate."
                      : "Attiva questo blocco solo se il progetto ha un vero prima/dopo da mostrare. Se disattivato, la sezione non appare sulla pagina progetto."}
                  </p>
                </div>
                {/* Toggle switch */}
                <button
                  type="button"
                  role="switch"
                  aria-checked={pForm.ba_enabled}
                  onClick={() => setPForm((s) => ({ ...s, ba_enabled: !s.ba_enabled }))}
                  data-testid="ba-enabled-toggle"
                  className={`shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${pForm.ba_enabled ? "bg-primary" : "bg-muted-foreground/40"}`}
                  title={pForm.ba_enabled ? "Disattiva Prima/Dopo" : "Attiva Prima/Dopo"}
                >
                  <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${pForm.ba_enabled ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </div>
              {pForm.ba_enabled && (<>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="font-mono text-xs uppercase tracking-widest">URL foto &quot;prima&quot;</Label>
                  <div className="flex gap-2 items-center">
                    <ImageThumb src={pForm.ba_before} size={40} alt="prima" />
                    <Input data-testid="project-ba-before" value={pForm.ba_before} onChange={(e) => setPForm((s) => ({ ...s, ba_before: e.target.value }))} placeholder="/images/..." className="rounded-none flex-1" />
                    <UploadButton testId="upload-ba-before" label="" onUploaded={(url) => setPForm((s) => ({ ...s, ba_before: url }))} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="font-mono text-xs uppercase tracking-widest">URL foto &quot;dopo&quot;</Label>
                  <div className="flex gap-2 items-center">
                    <ImageThumb src={pForm.ba_after} size={40} alt="dopo" />
                    <Input data-testid="project-ba-after" value={pForm.ba_after} onChange={(e) => setPForm((s) => ({ ...s, ba_after: e.target.value }))} placeholder="/images/..." className="rounded-none flex-1" />
                    <UploadButton testId="upload-ba-after" label="" onUploaded={(url) => setPForm((s) => ({ ...s, ba_after: url }))} />
                  </div>
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label className="font-mono text-xs uppercase tracking-widest">Titolo del confronto</Label>
                  <Input data-testid="project-ba-label" value={pForm.ba_label} onChange={(e) => setPForm((s) => ({ ...s, ba_label: e.target.value }))} placeholder="Dal cantiere alla facciata ultimata" className="rounded-none" />
                </div>
                <div className="space-y-1.5">
                  <Label className="font-mono text-xs uppercase tracking-widest">Etichetta &quot;prima&quot;</Label>
                  <Input data-testid="project-ba-blabel" value={pForm.ba_before_label} onChange={(e) => setPForm((s) => ({ ...s, ba_before_label: e.target.value }))} placeholder="PRIMA" className="rounded-none" />
                </div>
                <div className="space-y-1.5">
                  <Label className="font-mono text-xs uppercase tracking-widest">Etichetta &quot;dopo&quot;</Label>
                  <Input data-testid="project-ba-alabel" value={pForm.ba_after_label} onChange={(e) => setPForm((s) => ({ ...s, ba_after_label: e.target.value }))} placeholder="DOPO" className="rounded-none" />
                </div>
              </div>
              </>)}
            </div>

            {/* Flags */}
            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" data-testid="project-wip" checked={pForm.wip} onChange={(e) => setPForm((s) => ({ ...s, wip: e.target.checked }))} className="w-4 h-4" />
                <span className="font-mono text-xs uppercase tracking-widest">Work in Progress</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" data-testid="project-featured" checked={pForm.featured} onChange={(e) => setPForm((s) => ({ ...s, featured: e.target.checked }))} className="w-4 h-4" />
                <span className="font-mono text-xs uppercase tracking-widest">In evidenza</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <button onClick={saveProject} data-testid="save-project-btn" disabled={saving} className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-mono text-xs uppercase tracking-widest px-6 py-3 hover:bg-foreground transition-colors disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DETAIL MODAL — quote or career */}
      <Dialog open={!!selectedItem} onOpenChange={(o) => !o && setSelectedItem(null)}>
        <DialogContent className="rounded-none max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="detail-dialog">
          {selectedItem && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3 mb-2">
                  <span className="font-mono text-[10px] uppercase tracking-widest bg-primary text-primary-foreground px-2 py-1">
                    {selectedItem.type === "quote" ? "Richiesta preventivo" : "Candidatura"}
                  </span>
                  {selectedItem.data.status && (
                    <Badge className={`rounded-none font-mono text-[10px] uppercase tracking-widest ${STATUS_STYLE[selectedItem.data.status] || ""}`}>
                      {selectedItem.data.status}
                    </Badge>
                  )}
                </div>
                <DialogTitle className="font-head font-black text-2xl md:text-3xl tracking-tightest">
                  {selectedItem.data.name}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-5 py-2">
                {/* Contact block */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="border border-border p-4">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Email</p>
                    <button type="button" onClick={() => openReply(selectedItem.type, selectedItem.data)} data-testid="detail-email-link" className="font-mono text-sm flex items-center gap-2 md:hover:text-primary transition-colors break-all text-left">
                      <Mail className="w-4 h-4 shrink-0" /> {selectedItem.data.email}
                    </button>
                  </div>
                  <div className="border border-border p-4">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Telefono</p>
                    <a href={`tel:${(selectedItem.data.phone || "").replace(/\s/g, "")}`} data-testid="detail-tel-link" className="font-mono text-sm flex items-center gap-2 md:hover:text-primary transition-colors">
                      <Phone className="w-4 h-4 shrink-0" /> {selectedItem.data.phone || "—"}
                    </a>
                  </div>
                </div>

                {/* Type-specific fields */}
                {selectedItem.type === "quote" ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="border border-border p-4">
                      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Servizio richiesto</p>
                      <p className="text-sm">{selectedItem.data.service}</p>
                    </div>
                    <div className="border border-border p-4">
                      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Località</p>
                      <p className="text-sm flex items-center gap-2"><MapPin className="w-4 h-4 shrink-0" /> {selectedItem.data.location || "—"}</p>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="border border-border p-4">
                      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Mansione</p>
                      <p className="text-sm">{selectedItem.data.role || "—"}</p>
                    </div>
                    <div className="border border-border p-4">
                      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Esperienza</p>
                      <p className="text-sm">{selectedItem.data.experience || "—"}</p>
                    </div>
                    {selectedItem.data.availability && (
                      <div className="border border-border p-4 sm:col-span-2">
                        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Disponibilità</p>
                        <p className="text-sm">{selectedItem.data.availability}</p>
                      </div>
                    )}
                    {selectedItem.data.cv_url && (
                      <div className="border border-border p-4 sm:col-span-2 bg-primary/5">
                        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Curriculum Vitae</p>
                        <a
                          href={selectedItem.data.cv_url}
                          download={selectedItem.data.cv_filename || undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="detail-cv-download"
                          className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-mono text-xs uppercase tracking-widest px-4 py-2 md:hover:bg-foreground transition-colors"
                        >
                          <FileText className="w-4 h-4" /> Scarica {selectedItem.data.cv_filename || "CV"}
                        </a>
                      </div>
                    )}
                  </div>
                )}

                {/* Message */}
                {selectedItem.data.message && (
                  <div className="border border-border p-4 bg-muted/30">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Messaggio</p>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="detail-full-message">
                      {selectedItem.data.message}
                    </p>
                  </div>
                )}

                {/* Meta */}
                <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
                  <Clock className="w-3 h-3" /> Ricevuta il {fmtDate(selectedItem.data.created_at)}
                </p>
                {(() => {
                  const r = retentionInfo(selectedItem.data.created_at);
                  if (!r) return null;
                  return (
                    <p data-testid="detail-retention" className={`font-mono text-[10px] uppercase tracking-widest flex items-center gap-1.5 ${r.state === "expired" ? "text-destructive" : r.state === "expiring" ? "text-amber-600" : "text-muted-foreground"}`}>
                      <AlertTriangle className="w-3 h-3" />
                      Scadenza conservazione: {r.expiry.toLocaleDateString("it-IT")}
                      {r.state === "expired" ? " — scaduta, cancellare i dati" : r.state === "expiring" ? ` — tra ${r.daysLeft} giorni` : ""}
                    </p>
                  );
                })()}
              </div>

              <DialogFooter>
                <div className="flex flex-wrap gap-2 w-full">
                  <button
                    type="button"
                    onClick={() => openReply(selectedItem.type, selectedItem.data)}
                    data-testid="detail-reply-btn"
                    className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-mono text-xs uppercase tracking-widest px-5 py-3 md:hover:bg-foreground transition-colors"
                  >
                    <Mail className="w-4 h-4" /> Rispondi
                  </button>
                  {selectedItem.data.phone && (
                    <a
                      href={`tel:${selectedItem.data.phone.replace(/\s/g, "")}`}
                      data-testid="detail-call-btn"
                      className="inline-flex items-center gap-2 border border-border font-mono text-xs uppercase tracking-widest px-5 py-3 md:hover:border-foreground transition-colors"
                    >
                      <Phone className="w-4 h-4" /> Chiama
                    </a>
                  )}
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <ReplyDialog
        open={replyState.open}
        onOpenChange={(o) => setReplyState((s) => ({ ...s, open: o }))}
        email={replyState.email}
        kind={replyState.kind}
        item={replyState.item}
      />
    </div>
  );
}
